import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import DocAttachTrigger from "@/components/DocAttachTrigger";
import HybridResearchPanel from "@/components/HybridResearchPanel";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";
import {
  Inbox, Filter, Phone, CheckCircle2, XCircle, Clock, ExternalLink,
  TrendingUp, Euro, Mail, Building2, RefreshCw, Plus, Edit2, Trash2, X, Loader2,
} from "lucide-react";

const STATUS = {
  nouveau: { label: "Nouveau", color: "#378ADD", bg: "rgba(55,138,221,0.12)", icon: Clock },
  contacte: { label: "Contacté", color: "#EF9F27", bg: "rgba(239,159,39,0.12)", icon: Phone },
  converti: { label: "Converti", color: "#1D9E75", bg: "rgba(29,158,117,0.12)", icon: CheckCircle2 },
  ecarte: { label: "Écarté", color: "#E24B4A", bg: "rgba(226,75,74,0.12)", icon: XCircle },
};

const VERDICT_COLOR = {
  "à structurer": "#1D9E75",
  "à qualifier": "#378ADD",
  "à creuser": "#EF9F27",
  "à écarter": "#E24B4A",
};

function scoreColor(s) {
  if (s >= 70) return "#1D9E75";
  if (s >= 50) return "#378ADD";
  if (s >= 35) return "#EF9F27";
  return "#E24B4A";
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function formatTicket(t) {
  if (!t && t !== 0) return "—";
  if (t < 1) return `${(t * 1000).toFixed(0)} k€`;
  return `${t.toFixed(t < 10 ? 1 : 0)} M€`;
}

export default function Leads() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/leads").then((r) => setData(r.data)).catch(() => setData({ leads: [], stats: { total: 0 } }));
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setUpdating(id);
    try {
      await api.patch(`/leads/${id}`, { status });
      load();
    } finally {
      setUpdating(null);
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Supprimer définitivement ce lead ?")) return;
    setUpdating(id);
    try { await api.delete(`/leads/${id}`); notify.success("Lead supprimé."); load(); }
    catch (e) { notify.error(fmtError(e, "Suppression impossible.")); }
    finally { setUpdating(null); }
  };

  const convertToOpportunity = async (id) => {
    setUpdating(id);
    try {
      await api.post(`/leads/${id}/convert`, {});
      load();
      notify.success("Opportunité créée dans Origination. Le lead est marqué Converti.");
    } catch (e) {
      notify.error(fmtError(e, "Conversion impossible."));
    } finally {
      setUpdating(null);
    }
  };

  if (!data) return <div className="text-zinc-400">Chargement…</div>;

  const leads = filter === "all" ? data.leads : data.leads.filter((l) => (l.status || "nouveau") === filter);
  const s = data.stats;

  return (
    <div className="space-y-6" data-testid="leads-root">
      <ThreeQPageHeader endpoint="/workspace/leads/top-priority" />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#378ADD] noam-overline">
            <Inbox className="w-3 h-3" /> Stratégique · Leads capturés
          </div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">Leads entrants</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
            Prospects qualifiés automatiquement par Noam (NOAM Sonnet 4.5) depuis le formulaire public.
            Priorisez les scores élevés, contactez, convertissez en opportunité d'origination.
          </p>
        </div>
        <button onClick={load} className="btn-ghost text-xs" data-testid="leads-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Actualiser
        </button>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="leads-stat-total" icon={Inbox} label="Leads total" value={s.total} color="#378ADD" />
        <StatCard testid="leads-stat-avg" icon={TrendingUp} label="Score moyen" value={s.avg_score || 0} suffix="/100" color={scoreColor(s.avg_score || 0)} />
        <StatCard testid="leads-stat-ticket" icon={Euro} label="Ticket cumulé" value={`${s.ticket_total_mEUR || 0}`} suffix=" M€" color="#7F77DD" />
        <StatCard testid="leads-stat-nouveau" icon={Clock} label="À traiter" value={s.nouveau || 0} color="#EF9F27" />
      </div>

      {/* FILTERS */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-zinc-500" />
        <button onClick={() => setFilter("all")} className={`noam-pill text-xs ${filter === "all" ? "selected" : ""}`} data-testid="leads-filter-all">
          Tous ({s.total})
        </button>
        {Object.keys(STATUS).map((k) => (
          <button key={k} onClick={() => setFilter(k)} className={`noam-pill text-xs ${filter === k ? "selected" : ""}`} data-testid={`leads-filter-${k}`}>
            {STATUS[k].label} ({s[k] || 0})
          </button>
        ))}
      </div>

      {/* LIST */}
      {leads.length === 0 ? (
        <div className="noam-card p-12 text-center" data-testid="leads-empty">
          <Inbox className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
          <div className="text-zinc-400 text-sm">
            {s.total === 0
              ? "Aucun lead capturé pour le moment. Partagez le lien /qualifier."
              : "Aucun lead ne correspond à ce filtre."}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {leads.map((l) => {
            const status = STATUS[l.status || "nouveau"];
            const StatusIcon = status.icon;
            const score = l.ai_result?.score || 0;
            const isOpen = expanded === l.id;
            return (
              <div key={l.id} className="noam-card overflow-hidden" data-testid={`lead-row-${l.id}`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : l.id)}
                  className="w-full p-4 flex items-center gap-4 hover:bg-white/[0.02] transition-colors text-left"
                  data-testid={`lead-row-toggle-${l.id}`}
                >
                  <span
                    className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: `${scoreColor(score)}1A`, border: `1px solid ${scoreColor(score)}55` }}
                  >
                    <span className="noam-display text-base font-bold" style={{ color: scoreColor(score) }}>{score}</span>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-white truncate">{l.opportunity?.titre || "—"}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium capitalize" style={{ background: `${VERDICT_COLOR[l.ai_result?.verdict] || "#A1A1AA"}1A`, color: VERDICT_COLOR[l.ai_result?.verdict] || "#A1A1AA" }}>
                        {l.ai_result?.verdict || "—"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-zinc-500 mt-1 flex-wrap">
                      <span>{l.prenom} {l.nom}</span>
                      <span className="opacity-30">·</span>
                      <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {l.organisation || "—"}</span>
                      <span className="opacity-30">·</span>
                      <span>{l.opportunity?.secteur}</span>
                      <span className="opacity-30">·</span>
                      <span>{formatTicket(l.opportunity?.ticket_mEUR)}</span>
                      <span className="opacity-30">·</span>
                      <span>{formatDate(l.created_at)}</span>
                      {l.aging && l.aging.needs_refresh && (
                        <>
                          <span className="opacity-30">·</span>
                          <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold ${
                              l.aging.bucket === "dormant"
                                ? "bg-red-500/15 text-red-400 border border-red-400/40"
                                : l.aging.bucket === "stale"
                                ? "bg-orange-500/15 text-orange-400 border border-orange-400/40"
                                : "bg-yellow-500/10 text-yellow-400 border border-yellow-400/30"
                            }`}
                            data-testid={`lead-aging-${l.id}`}
                            title={`${l.aging.days_since} jours depuis le dernier contact — à rafraîchir`}
                          >
                            À rafraîchir · {l.aging.days_since}j
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <span
                    className="text-[11px] px-2.5 py-1 rounded-full font-medium flex items-center gap-1.5 shrink-0"
                    style={{ background: status.bg, color: status.color }}
                    data-testid={`lead-status-${l.id}`}
                  >
                    <StatusIcon className="w-3 h-3" /> {status.label}
                  </span>
                </button>

                {isOpen && (
                  <div className="border-t border-white/5 p-5 bg-white/[0.015]" data-testid={`lead-expanded-${l.id}`}>
                    <div className="grid md:grid-cols-2 gap-5 mb-5">
                      <div>
                        <div className="noam-overline mb-2">Contact</div>
                        <div className="text-sm space-y-1 text-zinc-300">
                          <div className="font-medium text-white">{l.prenom} {l.nom}</div>
                          <div className="flex items-center gap-1.5"><Mail className="w-3 h-3 text-zinc-500" /> {l.email}</div>
                          {l.organisation && <div className="flex items-center gap-1.5"><Building2 className="w-3 h-3 text-zinc-500" /> {l.organisation}</div>}
                        </div>
                      </div>
                      <div>
                        <div className="noam-overline mb-2">Opportunité</div>
                        <div className="text-sm space-y-1 text-zinc-300">
                          <div className="font-medium text-white">{l.opportunity?.titre}</div>
                          <div>{l.opportunity?.secteur} · {formatTicket(l.opportunity?.ticket_mEUR)}</div>
                          {l.opportunity?.description && <div className="text-xs text-zinc-400 mt-2">{l.opportunity.description}</div>}
                        </div>
                      </div>
                    </div>

                    {l.ai_result && (
                      <div className="space-y-4">
                        <p className="text-zinc-200 italic border-l-2 border-[#378ADD] pl-3 text-sm" data-testid={`lead-argumentaire-${l.id}`}>
                          {l.ai_result.argumentaire}
                        </p>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <div className="noam-overline mb-2">Forces</div>
                            <ul className="space-y-1 text-sm">
                              {(l.ai_result.forces || []).map((f, i) => (
                                <li key={`f-${l.id}-${i}`} className="text-[#1D9E75]">+ <span className="text-zinc-200">{f}</span></li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <div className="noam-overline mb-2">Risques</div>
                            <ul className="space-y-1 text-sm">
                              {(l.ai_result.risques || []).map((r, i) => (
                                <li key={`r-${l.id}-${i}`} className="text-[#E24B4A]">– <span className="text-zinc-200">{r}</span></li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <div className="text-xs text-zinc-500">
                          <span className="text-zinc-400">Prochaine étape suggérée :</span>{" "}
                          <span className="text-white">{l.ai_result.prochaine_etape}</span>
                        </div>
                      </div>
                    )}

                    {l.ai_probability != null && (
                      <div className="mt-4 p-3 rounded-md border border-[#EF9F27]/30 bg-[#EF9F27]/5" data-testid={`lead-ai-prob-${l.id}`}>
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-3.5 h-3.5 text-[#EF9F27]" />
                          <span className="text-[10px] uppercase tracking-wider text-[#EF9F27] font-semibold">Probabilité IA de closing</span>
                          <span className="text-2xl font-bold text-[#EF9F27] ml-auto">{l.ai_probability}%</span>
                        </div>
                        {l.ai_probability_rationale && (
                          <p className="text-xs text-zinc-300 mt-2">{l.ai_probability_rationale}</p>
                        )}
                      </div>
                    )}

                    {/* P2 — Hybrid web research per lead : signaux marché */}
                    <div className="mt-4 pt-4 border-t border-white/5">
                      <HybridResearchPanel
                        contextModule="leads"
                        contextId={l.id}
                        initialTopic={`${l.opportunity?.titre || ""} ${l.organisation || ""} ${l.opportunity?.secteur || ""}`.trim()}
                        defaultMode="hybrid"
                        title="Signaux marché pour ce deal"
                        testid={`lead-hybrid-${l.id}`}
                        compact
                      />
                    </div>

                    <div className="flex items-center gap-2 mt-6 pt-5 border-t border-white/5 flex-wrap">
                      <DocAttachTrigger module="pipeline" entityId={l.id} entityLabel={l.opportunity?.titre} />
                      <button
                        onClick={async () => {
                          setUpdating(l.id);
                          try {
                            const r = await api.post(`/workspace/leads/${l.id}/ai-probability`);
                            notify.success(`Probabilité IA : ${r.data.probability}%`);
                            await load();
                          } catch (err) { notify.error(fmtError(err, "Calcul IA indisponible.")); }
                          finally { setUpdating(null); }
                        }}
                        disabled={updating === l.id}
                        className="btn-ghost text-xs disabled:opacity-50"
                        data-testid={`lead-action-ai-prob-${l.id}`}
                      >
                        <TrendingUp className="w-3.5 h-3.5" /> Probabilité IA
                      </button>
                      <a href={`mailto:${l.email}?subject=Noam · Votre opportunité ${l.opportunity?.titre}`} className="btn-primary text-xs" data-testid={`lead-action-email-${l.id}`}>
                        <Mail className="w-3.5 h-3.5" /> Envoyer un email
                      </a>
                      {/* V23.15.76 — Rafraîchir un lead (mise à jour du last_contact_at) */}
                      <button
                        onClick={async () => {
                          setUpdating(l.id);
                          try {
                            await api.post(`/leads/${l.id}/mark-contacted`);
                            notify.success("Lead rafraîchi — dernier contact mis à jour.");
                            await load();
                          } catch (err) {
                            notify.error(fmtError(err, "Impossible de rafraîchir."));
                          } finally { setUpdating(null); }
                        }}
                        disabled={updating === l.id}
                        className="btn-ghost text-xs disabled:opacity-50"
                        data-testid={`lead-action-mark-contacted-${l.id}`}
                        title="Rafraîchir : marque ce lead comme recontacté aujourd'hui"
                      >
                        <TrendingUp className="w-3.5 h-3.5" /> Rafraîchir
                      </button>
                      {(l.ai_result?.score || 0) >= 60 && l.status !== "converti" && (
                        <button
                          onClick={() => convertToOpportunity(l.id)}
                          disabled={updating === l.id}
                          className="btn-ghost text-xs disabled:opacity-50 border-[#1D9E75]/40 text-[#1D9E75] hover:bg-[#1D9E75]/10"
                          data-testid={`lead-action-convert-${l.id}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Convertir en opportunité
                        </button>
                      )}
                      <button onClick={() => setStatus(l.id, "contacte")} disabled={updating === l.id} className="btn-ghost text-xs disabled:opacity-50" data-testid={`lead-action-contacte-${l.id}`}>
                        <Phone className="w-3.5 h-3.5" /> Marquer comme contacté
                      </button>
                      <button onClick={() => setStatus(l.id, "converti")} disabled={updating === l.id} className="btn-ghost text-xs disabled:opacity-50" data-testid={`lead-action-converti-${l.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Convertir (manuel)
                      </button>
                      <button onClick={() => setStatus(l.id, "ecarte")} disabled={updating === l.id} className="btn-ghost text-xs disabled:opacity-50" data-testid={`lead-action-ecarte-${l.id}`}>
                        <XCircle className="w-3.5 h-3.5" /> Écarter
                      </button>
                      {l.status !== "nouveau" && (
                        <button onClick={() => setStatus(l.id, "nouveau")} disabled={updating === l.id} className="text-xs text-zinc-500 hover:text-white ml-2" data-testid={`lead-action-reset-${l.id}`}>
                          Réinitialiser
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, suffix = "", testid }) {
  return (
    <div className="noam-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-zinc-400">
        <Icon className="w-3 h-3" style={{ color }} /> {label}
      </div>
      <div className="noam-display text-3xl font-bold mt-3" style={{ color }}>
        {value}<span className="text-base text-zinc-500">{suffix}</span>
      </div>
    </div>
  );
}
